/**
 * GovRAMP Controls Matrix / SSP workbook — V103-7-2 (D-103-8, D-103-14; ADR-0065, ADR-0066 §8).
 *
 * Fills GovRAMP's own pinned template (never a look-alike) with:
 *   - 3_Information System / 4_System Description — workspace + the caller's
 *     system information (dropdown values checked against the template);
 *   - 12_Mod Controls — per-program control records (ADR-0065: GovRAMP's own
 *     implementation statements, never another program's), the implementation
 *     status, the control provider / full inheritance derived from the
 *     responsibility matrix, and "Control Completed" from the period's
 *     reviewed determinations (objective readiness);
 *   - 10_CRM — the customer responsibility text on the program control record;
 *   - 11_Inventory / 7_Ports / 8_Interconnections — the V103-4-4 renderers over
 *     `getBoundaryRows` (the workbook stamps their rows_sha256);
 *   - 9_CIS Matrix is GovRAMP's formula sheet over 12_Mod Controls: never written.
 * Every write binds by header label; after the fill the template-shape
 * fingerprint must be unchanged (or nothing is signed).
 */
import { z } from 'zod';
import { getProgramManifest } from '../programs/manifests.js';
import { fillTemplate, templateFingerprint, TemplateError, } from '../xlsx/template-writer.js';
import { currentGovrampTemplate, INFORMATION_SYSTEM_FIELDS, normalizeControlId, OCM_CONTROLS, OCM_CRM, OCM_INTERCONNECTIONS, OCM_INVENTORY, OCM_PORTS, OCM_SHEETS, SYSTEM_DESCRIPTION_FIELD, } from '../xlsx/govramp-templates.js';
import { concatChunks } from '../xlsx/zip.js';
import { getBoundaryRows } from './boundary-rows-service.js';
import { renderGovrampInterconnections, renderGovrampInventory, renderGovrampPorts, resolveBoundaryOwnerNames } from './boundary-renderers.js';
import { deriveOrigination, listProgramControls } from './program-control-service.js';
import { listResponsibilities } from './responsibility-service.js';
import { computeObjectiveReadiness } from './objective-readiness-service.js';
import { issueFilePackage, storePackageFiles, XLSX_MEDIA_TYPE } from './file-package-service.js';
// ---- input -----------------------------------------------------------------------------------
const line = z.string().trim().min(1).max(500);
/** What the app doesn't know about the system; dropdown values are checked against the template itself. */
export const GovrampSystemInformation = z.object({
    information_system_name: line.optional(),
    version: z.string().trim().min(1).max(50).optional(),
    operational_status: line.optional(),
    cloud_service_model: line.optional(),
    cloud_deployment_model: line.optional(),
    digital_identity_level: line.optional(),
    authorization_path: line.optional(),
    system_description: z.string().trim().min(1).max(20_000).optional(),
    /** For inherited controls: the leveraged service model ("IaaS" | "PaaS" | "SaaS"). */
    inherited_service_model: z.enum(['IaaS', 'PaaS', 'SaaS']).optional(),
    csp: z.object({ organization_name: line.optional(), street_address: line.optional(), suite: line.optional(), city_state_zip: line.optional() }).strict().optional(),
    preparer: z.object({ organization_name: line.optional(), street_address: line.optional(), suite: line.optional(), city_state_zip: line.optional() }).strict().optional(),
    owner: z.object({ name: line.optional(), title: line.optional(), organization: line.optional(), address: line.optional(), phone: line.optional(), email: line.optional() }).strict().optional(),
    authorizing_official: z.object({ name: line.optional(), title: line.optional(), organization: line.optional(), address: line.optional(), phone: line.optional(), email: line.optional() }).strict().optional(),
    security_contact: z.object({ name: line.optional(), title: line.optional(), organization: line.optional(), address: line.optional(), phone: line.optional(), email: line.optional() }).strict().optional(),
}).strict();
// ---- vocabulary mapping (our records → GovRAMP's dropdown values) ----------------------------------
export const GOVRAMP_IMPLEMENTATION_STATUS = {
    implemented: 'Implemented', partially_implemented: 'Partially Implemented', planned: 'Planned',
    alternative: 'Alternative Implementation', not_applicable: 'Not Applicable',
};
/** Control Provider, derived from the responsibility matrix (ADR-0065 §1 origination). */
export const GOVRAMP_CONTROL_PROVIDER = {
    inherited: 'Inherited from pre-existing StateRAMP or FedRAMP Authorization',
    hybrid: 'Shared',
    customer: 'Service Provider System Specific',
};
export class GovrampWorkbookError extends Error {
    code;
    details;
    constructor(code, message, details) {
        super(message);
        this.code = code;
        this.details = details;
        this.name = 'GovrampWorkbookError';
    }
}
/** A period of a program that issues `kind` (workspace-scoped; not_found otherwise). */
export async function govrampPackagePeriod(db, workspaceId, periodId, kind) {
    const r = (await db.query(`SELECT p.period_id, p.profile_id, p.period_start, p.period_end, p.status, c.program,
            COALESCE(p.program_options_json, c.program_options_json) AS options_json
       FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId])).results[0];
    if (!r)
        throw new GovrampWorkbookError('not_found', 'Audit period not found.');
    if (!getProgramManifest(r.program)?.packages.kinds.includes(kind))
        throw new GovrampWorkbookError('invalid', `This program does not issue ${kind} packages.`);
    let options = {};
    try {
        options = JSON.parse(r.options_json || '{}');
    }
    catch {
        options = {};
    }
    const { options_json: _o, ...rest } = r;
    return { ...rest, options };
}
function statementText(statement, partsJson) {
    const parts = (() => { try {
        return JSON.parse(partsJson || '{}');
    }
    catch {
        return {};
    } })();
    const lines = [statement?.trim() ?? '', ...Object.keys(parts).sort().map((k) => `${k}. ${parts[k]}`)].filter(Boolean);
    return lines.join('\n');
}
/** Build every sheet write for the period (no template needed). */
export async function buildGovrampSspWorkbookPlan(db, input) {
    const content = input.contentDb ?? db;
    const now = input.now ?? new Date();
    const period = await govrampPackagePeriod(db, input.workspaceId, input.periodId, 'govramp_ssp_xlsx');
    const info = GovrampSystemInformation.parse(input.systemInformation ?? {});
    const ws = (await db.query(`SELECT name FROM workspaces WHERE workspace_id = ? LIMIT 1`, [input.workspaceId])).results[0];
    // Program controls: GovRAMP's own records (ADR-0065), the profile's tier set.
    const list = await listProgramControls(db, input.workspaceId, period.profile_id);
    const records = new Map((await db.query(`SELECT pc.requirement_id, pc.implementation_status, r.statement, r.parts_json, r.customer_responsibility
       FROM program_controls pc
       LEFT JOIN program_control_revisions r ON r.revision_id = pc.current_revision_id AND r.workspace_id = pc.workspace_id
      WHERE pc.workspace_id = ? AND pc.profile_id = ?`, [input.workspaceId, period.profile_id])).results.map((r) => [r.requirement_id, r]));
    // Origination from the responsibility matrix over each control's objectives.
    const parties = new Map((await listResponsibilities(db, input.workspaceId, period.profile_id)).map((a) => [a.objective_id, a.party]));
    const objectivesByReq = new Map();
    for (const o of (await content.query(`SELECT objective_id, requirement_id FROM assessment_objectives WHERE status = 'active' AND requirement_id LIKE 'nist-800-53:%' ORDER BY requirement_id, ordinal, objective_id`)).results) {
        if (!objectivesByReq.has(o.requirement_id))
            objectivesByReq.set(o.requirement_id, []);
        objectivesByReq.get(o.requirement_id).push(o.objective_id);
    }
    // Control Completed: the period's reviewed determinations (objective engine).
    let readinessNote = null;
    let met = null;
    try {
        const rd = await computeObjectiveReadiness(db, { workspaceId: input.workspaceId, periodId: input.periodId, contentDb: input.contentDb });
        met = new Set(rd.requirements.filter((r) => r.status === 'met').map((r) => r.requirement_id));
    }
    catch (e) {
        readinessNote = `Control Completed not filled: ${e.message}`;
    }
    const controlRows = [];
    const crmRows = [];
    let withStatement = 0;
    for (const c of list.controls) {
        const rec = records.get(c.requirement_id);
        const origination = deriveOrigination((objectivesByReq.get(c.requirement_id) ?? []).map((o) => parties.get(o) ?? null));
        const text = rec ? statementText(rec.statement, rec.parts_json) : '';
        if (text)
            withStatement++;
        const values = {
            control_provider: origination ? GOVRAMP_CONTROL_PROVIDER[origination] : undefined,
            implementation_status: rec ? GOVRAMP_IMPLEMENTATION_STATUS[rec.implementation_status] : undefined,
            fully_inherited: origination === 'inherited' ? (info.inherited_service_model ? `Yes (${info.inherited_service_model})` : undefined) : rec ? 'No' : undefined,
            sp_implementation: text || undefined,
            control_completed: met ? (met.has(c.requirement_id) ? 'Yes' : 'No') : undefined,
        };
        const key = normalizeControlId(c.ref);
        controlRows.push({ key, values });
        const customer = rec?.customer_responsibility?.trim();
        if (customer || rec?.implementation_status === 'implemented') {
            const model = info.cloud_service_model;
            crmRows.push({ key, values: {
                    inherited: customer ? 'Partial' : 'Yes',
                    guidance: customer || undefined,
                    iaas: model === 'IaaS' ? 'X' : undefined, paas: model === 'PaaS' ? 'X' : undefined, saas: model === 'SaaS' ? 'X' : undefined,
                } });
        }
    }
    // Boundary sheets from the single read model.
    const rows = await getBoundaryRows(db, input.workspaceId, period.profile_id, period.period_id, now);
    const inventory = renderGovrampInventory(rows, await resolveBoundaryOwnerNames(db, input.workspaceId, rows));
    const ports = renderGovrampPorts(rows);
    const ics = renderGovrampInterconnections(rows);
    const impact = typeof period.options.impact_level === 'string' ? period.options.impact_level : null;
    const formValues = {
        service_provider_name: info.csp?.organization_name ?? ws?.name,
        information_system_name: info.information_system_name,
        version: info.version,
        date: now.toISOString().slice(0, 10),
        impact_level: impact ? impact.charAt(0).toUpperCase() + impact.slice(1) : undefined,
        operational_status: info.operational_status,
        cloud_service_model: info.cloud_service_model,
        cloud_deployment_model: info.cloud_deployment_model,
        digital_identity_level: info.digital_identity_level,
        authorization_path: info.authorization_path,
    };
    for (const g of ['csp', 'preparer', 'owner', 'authorizing_official', 'security_contact']) {
        for (const [k, v] of Object.entries(info[g] ?? {}))
            formValues[`${g}_${k}`] = v;
    }
    const formFields = INFORMATION_SYSTEM_FIELDS.map((f) => ({ ...f, value: formValues[f.id] }));
    const writes = [
        { kind: 'form', sheet: OCM_SHEETS.informationSystem, labelColumn: 'A', fields: formFields },
        { kind: 'keyed', sheet: OCM_CONTROLS.sheet, headerRows: [...OCM_CONTROLS.headerRows], firstDataRow: OCM_CONTROLS.firstDataRow,
            key: { header: OCM_CONTROLS.keyHeader, normalize: normalizeControlId }, columns: OCM_CONTROLS.columns, rows: controlRows },
        { kind: 'keyed', sheet: OCM_CRM.sheet, headerRows: [...OCM_CRM.headerRows], firstDataRow: OCM_CRM.firstDataRow,
            key: { header: OCM_CRM.keyHeader, normalize: normalizeControlId }, columns: OCM_CRM.columns, rows: crmRows },
        { kind: 'table', ...OCM_INVENTORY, headerRows: [...OCM_INVENTORY.headerRows], rows: inventory.rows },
        { kind: 'table', ...OCM_PORTS, headerRows: [...OCM_PORTS.headerRows], rows: ports.rows },
        { kind: 'table', ...OCM_INTERCONNECTIONS, headerRows: [...OCM_INTERCONNECTIONS.headerRows], rows: ics.rows },
    ];
    if (info.system_description)
        writes.push({ kind: 'form', sheet: OCM_SHEETS.systemDescription, labelColumn: 'A', fields: [{ ...SYSTEM_DESCRIPTION_FIELD, value: info.system_description }] });
    return {
        period, writes, rows_sha256: rows.rows_sha256, boundary_source: rows.source,
        summary: {
            controls: list.controls.length, controls_with_statement: withStatement, controls_completed: met ? list.controls.filter((c) => met.has(c.requirement_id)).length : null,
            crm_rows: crmRows.length, inventory_rows: inventory.rows.length, ports: ports.rows.length, interconnections: ics.rows.length, readiness_note: readinessNote,
        },
    };
}
/** Fill the template and prove its shape is unchanged (the template-shape check). */
export function renderGovrampSspWorkbook(template, plan) {
    const res = fillTemplate(template, plan.writes);
    const before = templateFingerprint(template);
    const after = templateFingerprint(concatChunks(res.chunks));
    if (JSON.stringify(before) !== JSON.stringify(after))
        throw new TemplateError('template_mismatch', 'The filled workbook no longer matches the template shape; nothing was signed.');
    return { chunks: res.chunks, bytes: res.bytes };
}
/** Load a pinned template (by sha256) and check it is the pinned bytes. */
export async function loadPinnedTemplate(source, pin) {
    const bytes = await source.get(pin.sha256);
    if (!bytes)
        throw new GovrampWorkbookError('conflict', `The GovRAMP template ${pin.filename} (${pin.version}) is not uploaded yet; upload it from govramp.org first.`, { template: pin.key, version: pin.version, sha256: pin.sha256, action: 'upload_template' });
    const h = await crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(h), (b) => b.toString(16).padStart(2, '0')).join('');
    if (hex !== pin.sha256)
        throw new GovrampWorkbookError('conflict', `The stored GovRAMP template does not match its pinned sha256 (${pin.version}).`);
    return bytes;
}
/** Generate, store and sign the SSP workbook (kind `govramp_ssp_xlsx`). */
export async function issueGovrampSspWorkbook(db, input) {
    const now = input.now ?? new Date();
    const plan = await buildGovrampSspWorkbookPlan(db, { ...input, now });
    const pin = input.templatePin ?? currentGovrampTemplate('govramp-ocm-moderate');
    const template = await loadPinnedTemplate(input.templates, pin);
    const out = renderGovrampSspWorkbook(template, plan);
    const ws = (await db.query(`SELECT slug FROM workspaces WHERE workspace_id = ? LIMIT 1`, [input.workspaceId])).results[0];
    const files = await storePackageFiles(input.store, input.workspaceId, [{
            name: `GovRAMP-SSP-Workbook_${ws?.slug ?? 'workspace'}_${plan.period.period_start}_${plan.period.period_end}.xlsx`, media_type: XLSX_MEDIA_TYPE, chunks: out.chunks,
        }]);
    return issueFilePackage(db, {
        workspace_id: input.workspaceId, profile_id: plan.period.profile_id, kind: 'govramp_ssp_xlsx', package_id: input.packageId, files,
        extra: {
            program: plan.period.program,
            period: { period_id: plan.period.period_id, period_start: plan.period.period_start, period_end: plan.period.period_end, status: plan.period.status },
            tier: plan.period.options.tier ?? null, impact_level: plan.period.options.impact_level ?? null,
            template: { key: pin.key, version: pin.version, sha256: pin.sha256, source: pin.source },
            boundary: { rows_sha256: plan.rows_sha256, source: plan.boundary_source },
            summary: plan.summary,
        },
        sign: input.sign, created_by_user_id: input.createdByUserId, period_start: plan.period.period_start, period_end: plan.period.period_end,
        rules_version: pin.version, now,
    });
}
//# sourceMappingURL=govramp-ssp-workbook-service.js.map